import React from 'react';
import { X, FileText, CheckCircle2, Circle } from 'lucide-react';
import { ProjectFile } from '@/types';
import { cn } from '@/lib/utils';

interface ExistingAttachmentTileProps {
  file: ProjectFile;
  isAndroid: boolean;
  selectionMode: boolean;
  isSelected: boolean;
  /** Result of the parent's `tileHandlers(key)` — spread onto the tile. */
  tileProps: React.HTMLAttributes<HTMLDivElement>;
  onRemove: () => void;
  /** Non-Android only: toggle this tile's selection from the hover/selected circle. */
  onToggleSelect?: () => void;
}

// Renders a single existing (already-uploaded) attachment tile. Selection-key,
// long-press, and removal wiring is computed by the parent and passed in via props
// so this component stays purely presentational.
export default function ExistingAttachmentTile({
  file,
  isAndroid,
  selectionMode,
  isSelected,
  tileProps,
  onRemove,
  onToggleSelect,
}: ExistingAttachmentTileProps) {
  const isImg = file.type === 'IMAGE' || (typeof file.type === 'string' && file.type.startsWith('image/'));
  const isVid = file.type === 'VIDEO' || (typeof file.type === 'string' && file.type.startsWith('video/'));
  const previewSrc = file.thumbnailUrl || file.url;

  return (
    <div
      {...tileProps}
      className={cn(
        'relative aspect-square bg-secondary rounded-lg overflow-hidden group',
        isAndroid && 'cursor-pointer select-none',
        isSelected && 'ring-2 ring-primary'
      )}
    >
      {(isImg || isVid) && previewSrc ? (
        <img
          src={previewSrc}
          alt={file.name}
          className="w-full h-full object-cover pointer-events-none"
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center p-2 pointer-events-none">
          <FileText className="w-6 h-6 text-muted-foreground mb-1" />
          <span className="text-xs text-muted-foreground text-center truncate w-full">
            {file.name}
          </span>
        </div>
      )}
      {/* Selected overlay — both platforms (Android only selects in selectionMode). */}
      {isSelected && <div className="absolute inset-0 bg-primary/20 pointer-events-none" />}

      {isAndroid && selectionMode ? (
        /* Android: tap on the tile toggles selection, so the indicator is non-interactive. */
        <div className="absolute top-1 right-1 pointer-events-none">
          {isSelected ? (
            <CheckCircle2 className="w-6 h-6 text-primary fill-primary-foreground" />
          ) : (
            <Circle className="w-6 h-6 text-white/90 drop-shadow" />
          )}
        </div>
      ) : (
        <button
          onClick={() => onRemove()}
          className="absolute top-1 left-1 w-6 h-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      {/* Non-Android: hover/selected select-circle (top-right). Only this toggles selection. */}
      {!isAndroid && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect?.();
          }}
          className={cn(
            'absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity',
            isSelected && 'opacity-100'
          )}
          aria-label={isSelected ? 'בטל בחירה' : 'בחר'}
        >
          {isSelected ? (
            <CheckCircle2 className="w-6 h-6 text-primary fill-primary-foreground" />
          ) : (
            <Circle className="w-6 h-6 text-white/90 drop-shadow" />
          )}
        </button>
      )}
    </div>
  );
}
